import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import {
  codeVaultService,
  CodeVaultAccessRecord,
} from "../services/codeVaultService";

export interface CodeVaultAccessState {
  loading: boolean;
  error: string | null;
  access: CodeVaultAccessRecord | null;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  isApprover: boolean;
  isReviewer: boolean;
}

const INITIAL: CodeVaultAccessState = {
  loading: true,
  error: null,
  access: null,
  canRead: false,
  canWrite: false,
  canDelete: false,
  isApprover: false,
  isReviewer: false,
};

const CodeVaultAccessContext = createContext<CodeVaultAccessState>(INITIAL);

export const useCodeVaultAccess = () => useContext(CodeVaultAccessContext);

export const CodeVaultAccessProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const [state, setState] = useState<CodeVaultAccessState>(INITIAL);

  useEffect(() => {
    let mounted = true;
    const fetchAccess = async () => {
      if (!user?.id) {
        setState((prev) => ({
          ...prev,
          loading: false,
          access: null,
          canRead: false,
          canWrite: false,
          canDelete: false,
        }));
        return;
      }
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const access = await codeVaultService.getUserAccess(user.id);
        if (!mounted) return;
        setState({
          loading: false,
          error: null,
          access,
          canRead: !!access?.read_access || !!user.isadmin,
          canWrite: !!access?.write_access || !!user.isadmin,
          canDelete: !!access?.delete_access || !!user.isadmin,
          isApprover: !!access?.is_approver || !!user.isadmin,
          isReviewer: !!access?.is_reviewer || !!user.isadmin,
        });
      } catch (e: any) {
        if (!mounted) return;
        setState({
          loading: false,
          error: e?.message || "Failed to load CodeVault access",
          access: null,
          canRead: !!user?.isadmin,
          canWrite: !!user?.isadmin,
          canDelete: !!user?.isadmin,
          isApprover: !!user.isadmin,
          isReviewer: !!user.isadmin,
        });
      }
    };

    fetchAccess();
    return () => {
      mounted = false;
    };
  }, [user?.id, user?.isadmin]);

  const value = useMemo(() => state, [state]);
  return (
    <CodeVaultAccessContext.Provider value={value}>
      {children}
    </CodeVaultAccessContext.Provider>
  );
};
